import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCwIcon,
  Trash2Icon,
  PlayIcon,
  SquareIcon,
  UnplugIcon,
  ServerIcon,
  ClockIcon,
  DollarSignIcon,
  LayersIcon,
  CopyIcon,
  TerminalIcon,
  SaveIcon,
  PencilIcon,
  InfoIcon,
} from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import {
  AdminInfraService,
  InfraInstanceDetail,
  VastAdapter,
} from "../../services/AdminInfraService";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tooltip } from "../ui/tooltip";
import {
  TemplateKwargsRow,
  templateKwargsObjectFromRows,
  templateKwargsRowsFromObject,
} from "./vllmTemplateKwargs";

const STATUS_VARIANT: Record<string, string> = {
  RUNNING: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  PROVISIONING: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  PENDING: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  STOPPED: "bg-muted text-muted-foreground border-border",
  DESTROYED: "bg-muted text-muted-foreground border-border",
  ERROR: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
};

function formatTimestamp(ts: string | undefined | null): string {
  if (!ts) return "—";
  const d = new Date(ts.endsWith("Z") ? ts : ts + "Z");
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function formatDuration(seconds: number | undefined | null): string {
  if (!seconds || seconds <= 0) return "—";
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function adapterLabel(a: VastAdapter): string {
  const runTag = a.finetune_run_id ? `Run #${a.finetune_run_id}` : "No run";
  return `${runTag}: ${a.hosted_name || a.model_name}`;
}

function CopyableField({
  label,
  value,
  masked = false,
}: {
  label: string;
  value: string;
  masked?: boolean;
}) {
  const [show, setShow] = React.useState(!masked);
  const copy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <div>
      <p className="text-sm text-muted-foreground font-gc-reg mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-3 py-1.5 rounded flex-1 overflow-x-auto select-all">
          {show ? value : "••••••••••••••••"}
        </code>
        {masked && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 shrink-0"
            onClick={() => setShow((s) => !s)}
          >
            {show ? "Hide" : "Show"}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 shrink-0"
          onClick={copy}
        >
          <CopyIcon className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground font-gc-reg mb-0.5">{label}</p>
      <p className="text-base font-gc-med text-foreground">{value || "—"}</p>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5 flex items-start gap-3">
        <div className="p-2 rounded-md bg-muted">
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground font-gc-reg">{label}</p>
          <p className="text-lg font-gc-demi-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

interface VllmConfigFieldDef {
  key: string;
  label: string;
  type: "text" | "select" | "bool";
  options?: string[];
  placeholder?: string;
  defaultValue?: string;
}

const VLLM_CONFIG_FIELDS: VllmConfigFieldDef[] = [
  {
    key: "tokenizer",
    label: "tokenizer (HF override)",
    type: "text",
    placeholder: "e.g. Qwen/Qwen3.5-27B for unsloth/Qwen3.5-27B",
  },
  { key: "max_model_len", label: "max-model-len", type: "text", placeholder: "16384" },
  { key: "max_num_seqs", label: "max-num-seqs", type: "text", placeholder: "64" },
  { key: "max_num_batched_tokens", label: "max-num-batched-tokens", type: "text", placeholder: "16384" },
  { key: "kv_cache_dtype", label: "kv-cache-dtype", type: "select", options: ["auto", "fp8", "fp8_e5m2", "fp8_e4m3"], defaultValue: "auto" },
  { key: "load_format", label: "load-format", type: "select", options: ["auto", "safetensors", "pt", "mistral"], defaultValue: "auto" },
  { key: "tokenizer_mode", label: "tokenizer-mode", type: "select", options: ["auto", "slow", "mistral"], defaultValue: "auto" },
  { key: "config_format", label: "config-format", type: "select", options: ["auto", "hf", "mistral"], defaultValue: "auto" },
  { key: "gdn_prefill_backend", label: "gdn-prefill-backend", type: "select", options: ["auto", "triton", "flashinfer"], defaultValue: "auto" },
  { key: "tensor_parallel_size", label: "tensor-parallel-size", type: "text", placeholder: "1" },
  { key: "trust_remote_code", label: "trust-remote-code", type: "bool" },
  { key: "enable_lora", label: "enable-lora", type: "bool" },
  { key: "enable_auto_tool_choice", label: "enable-auto-tool-choice", type: "bool" },
  { key: "tool_call_parser", label: "tool-call-parser", type: "select", options: ["llama3_json", "hermes", "mistral", "pythonic"], defaultValue: "llama3_json" },
];

function VllmConfigCard({
  detail,
  apiKey,
  onSaved,
}: {
  detail: InfraInstanceDetail;
  apiKey: any;
  onSaved: () => void;
}) {
  const isStopped = detail.status === "STOPPED";
  const savedConfig = useMemo(() => detail.vllm_config || {}, [detail]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [templateKwargsRows, setTemplateKwargsRows] = useState<TemplateKwargsRow[]>(
    []
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const filled: Record<string, unknown> = { ...savedConfig };
    for (const f of VLLM_CONFIG_FIELDS) {
      if (filled[f.key] === undefined || filled[f.key] === null) {
        if (f.type === "select" && f.defaultValue) filled[f.key] = f.defaultValue;
        if (f.type === "bool") filled[f.key] = filled[f.key] ?? true;
      }
    }
    setDraft(filled);
    setEditing(false);
    setTemplateKwargsRows([]);
  }, [savedConfig]);

  const effectiveDraft = useMemo(() => {
    if (!editing) return draft;
    const d = { ...draft };
    const o = templateKwargsObjectFromRows(templateKwargsRows);
    if (o) d.default_chat_template_kwargs = o;
    else delete d.default_chat_template_kwargs;
    return d;
  }, [draft, editing, templateKwargsRows]);

  const hasChanges = useMemo(
    () => JSON.stringify(effectiveDraft) !== JSON.stringify(savedConfig),
    [effectiveDraft, savedConfig]
  );

  const setField = (key: string, val: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: val }));
  };

  const saveConfig = () => {
    setSaving(true);
    AdminInfraService.updateVllmConfig(detail.id, effectiveDraft, null, apiKey).subscribe(
      () => {
        toast.success("vLLM config saved — will apply on next start");
        setSaving(false);
        setEditing(false);
        onSaved();
      },
      (err: any) => {
        toast.error(err?.response?.message || "Failed to save config");
        setSaving(false);
      }
    );
  };

  const cancelEdit = () => {
    setDraft({ ...savedConfig });
    setTemplateKwargsRows([]);
    setEditing(false);
  };

  if (!savedConfig || Object.keys(savedConfig).length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-gc-demi-bold text-foreground">
            vLLM Configuration
          </h2>
          {isStopped && !editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTemplateKwargsRows(
                  templateKwargsRowsFromObject(draft.default_chat_template_kwargs)
                );
                setEditing(true);
              }}
            >
              <PencilIcon className="w-4 h-4 mr-1.5" />
              Edit
            </Button>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saving || !hasChanges}
                onClick={saveConfig}
              >
                {saving ? (
                  <RefreshCwIcon className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <SaveIcon className="w-4 h-4 mr-1.5" />
                )}
                Save
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
            {VLLM_CONFIG_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                {f.type === "select" ? (
                  <Select
                    value={String(draft[f.key] ?? "")}
                    onValueChange={(v) => setField(f.key, v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "bool" ? (
                  <Select
                    value={draft[f.key] === true ? "true" : draft[f.key] === false ? "false" : ""}
                    onValueChange={(v) => setField(f.key, v === "true")}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="mt-1"
                    value={String(draft[f.key] ?? "")}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setField(f.key, raw === "" ? undefined : isNaN(Number(raw)) ? raw : Number(raw));
                    }}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">Default chat template kwargs</Label>
                  <Tooltip content="JSON object for vLLM --default-chat-template-kwargs. Values parse as JSON when valid (e.g. false, true); otherwise kept as strings. Empty list omits the field. Applied on next start.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setTemplateKwargsRows((prev) => [...prev, { key: "", value: "" }])
                  }
                >
                  Add pair
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {templateKwargsRows.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      className="flex-1 font-mono text-xs"
                      placeholder="Key (e.g. enable_thinking)"
                      value={row.key}
                      onChange={(e) => {
                        const next = [...templateKwargsRows];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setTemplateKwargsRows(next);
                      }}
                    />
                    <Input
                      className="flex-1 font-mono text-xs"
                      placeholder='Value (e.g. false)'
                      value={row.value}
                      onChange={(e) => {
                        const next = [...templateKwargsRows];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setTemplateKwargsRows(next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-destructive"
                      onClick={() =>
                        setTemplateKwargsRows((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <pre className="text-sm bg-muted p-4 rounded-md overflow-x-auto font-mono">
            {JSON.stringify(savedConfig, null, 2)}
          </pre>
        )}

        {editing && hasChanges && (
          <p className="text-xs text-muted-foreground mt-3">
            Changes will be applied when the instance is next started.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const InstanceAdminDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const infraId = id ? Number(id) : NaN;
  const [detail, setDetail] = useState<InfraInstanceDetail | null>(null);
  const [logText, setLogText] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    | { kind: "start" }
    | { kind: "stop" }
    | { kind: "destroy" }
    | { kind: "offload"; adapter: VastAdapter }
    | null
  >(null);

  const loadDetail = useCallback(() => {
    if (!Number.isFinite(infraId)) return;
    setLoading(true);
    AdminInfraService.getInstance(infraId, null, apiKey).subscribe(
      (d: InfraInstanceDetail) => {
        setDetail(d);
        setLoading(false);
      },
      () => {
        toast.error("Failed to load instance");
        setLoading(false);
      }
    );
  }, [apiKey, infraId]);

  const loadLogs = useCallback(() => {
    if (!Number.isFinite(infraId)) return;
    AdminInfraService.getLogs(infraId, setLogsLoading, apiKey).subscribe(
      (res: { log: string }) => setLogText(res.log || ""),
      (err: any) => setLogText(`(failed to fetch logs: ${err?.message || err})`)
    );
  }, [apiKey, infraId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const executeAction = () => {
    if (!confirmAction) return;
    setActionLoading(true);
    const done = () => setActionLoading(false);

    switch (confirmAction.kind) {
      case "start":
        AdminInfraService.startInstance(infraId, null, apiKey).subscribe(
          () => { toast.success("Start requested"); done(); loadDetail(); },
          () => { toast.error("Start failed"); done(); }
        );
        break;
      case "stop":
        AdminInfraService.stopInstance(infraId, null, apiKey).subscribe(
          () => { toast.success("Stop requested"); done(); loadDetail(); },
          () => { toast.error("Stop failed"); done(); }
        );
        break;
      case "destroy":
        AdminInfraService.destroyInstance(infraId, false, null, apiKey).subscribe(
          (result: { status: string }) => {
            done();
            if (result.status === "not_found_on_vast") {
              toast.message("Not on Vast — use force destroy from list if needed");
              return;
            }
            toast.success("Instance destroyed");
            navigate("/admin/instances");
          },
          () => { toast.error("Destroy failed"); done(); }
        );
        break;
      case "offload":
        AdminInfraService.removeAdapter(infraId, confirmAction.adapter.model_id, null, apiKey).subscribe(
          () => { toast.success("Adapter offloaded"); done(); loadDetail(); },
          () => { toast.error("Offload failed"); done(); }
        );
        break;
    }
    setConfirmAction(null);
  };

  if (!Number.isFinite(infraId)) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <p className="text-muted-foreground">Invalid instance id.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-6">
        {/* Header Card */}
        <Card>
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    className="text-muted-foreground hover:text-maitai-green"
                    onClick={() => navigate("/admin/instances")}
                  >
                    Instances
                  </button>
                </li>
                <li className="text-muted-foreground">/</li>
                <li className="text-maitai-green font-gc-med">
                  {detail?.description || `#${infraId}`}
                </li>
              </ol>
            </nav>

            {loading && !detail ? (
              <div className="flex items-center text-muted-foreground py-8">
                <RefreshCwIcon className="w-5 h-5 animate-spin mr-2" />
                Loading...
              </div>
            ) : !detail ? (
              <p className="text-muted-foreground">Instance not found.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                        {detail.description || `Instance ${detail.id}`}
                      </h1>
                      <Badge variant="outline" className="capitalize text-sm">
                        {detail.provider}
                      </Badge>
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                          STATUS_VARIANT[detail.status] || STATUS_VARIANT.STOPPED
                        }`}
                      >
                        {detail.status}
                      </span>
                    </div>
                    <p className="text-lg text-muted-foreground font-gc-reg">
                      Vast id: {detail.instance_id || "—"}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={loadDetail} disabled={loading || actionLoading}>
                      <RefreshCwIcon className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                    {detail.status === "STOPPED" && (
                      <Button
                        variant="default"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => setConfirmAction({ kind: "start" })}
                      >
                        {actionLoading ? (
                          <RefreshCwIcon className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <PlayIcon className="w-4 h-4 mr-1.5" />
                        )}
                        Start
                      </Button>
                    )}
                    {detail.status === "RUNNING" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => setConfirmAction({ kind: "stop" })}
                      >
                        {actionLoading ? (
                          <RefreshCwIcon className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <SquareIcon className="w-4 h-4 mr-1.5" />
                        )}
                        Stop
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={actionLoading}
                      onClick={() => setConfirmAction({ kind: "destroy" })}
                    >
                      {actionLoading ? (
                        <RefreshCwIcon className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <Trash2Icon className="w-4 h-4 mr-1.5" />
                      )}
                      Destroy
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {detail && (
          <>
            {/* Stats Row */}
            <div className="grid md:grid-cols-4 gap-4">
              <StatCard
                icon={ServerIcon}
                label="GPU"
                value={detail.gpu_type ? `${detail.gpu_count}x ${detail.gpu_type}` : "—"}
              />
              <StatCard
                icon={DollarSignIcon}
                label="Cost / hr"
                value={detail.cost_per_hour != null ? `$${detail.cost_per_hour.toFixed(2)}` : "—"}
              />
              <StatCard
                icon={ClockIcon}
                label="Running Time"
                value={formatDuration(detail.total_running_seconds)}
              />
              <StatCard
                icon={LayersIcon}
                label="Adapters"
                value={`${detail.adapter_count}/${detail.max_adapter_count}`}
              />
            </div>

            {/* Details Card */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-lg font-gc-demi-bold text-foreground mb-6">Details</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                  <DetailField label="Base Model" value={detail.base_model} />
                  <DetailField label="HF Model" value={detail.hf_model_id} />
                  <DetailField label="Template Hash" value={detail.template_hash_id} />
                  <DetailField label="Created" value={formatTimestamp(detail.date_created)} />
                  <DetailField label="Updated" value={formatTimestamp(detail.date_updated)} />
                  <DetailField
                    label="Estimated Cost"
                    value={
                      detail.estimated_cost_usd != null && detail.estimated_cost_usd > 0
                        ? `$${detail.estimated_cost_usd.toFixed(2)}`
                        : "—"
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Connection Info */}
            {detail.connection_info && (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <TerminalIcon className="w-5 h-5 text-muted-foreground" />
                    <h2 className="text-lg font-gc-demi-bold text-foreground">Connection</h2>
                  </div>
                  <div className="space-y-3">
                    {detail.connection_info.ssh_command && (
                      <CopyableField
                        label="SSH"
                        value={detail.connection_info.ssh_command}
                      />
                    )}
                    {detail.connection_info.vllm_url && (
                      <CopyableField
                        label="vLLM API"
                        value={detail.connection_info.vllm_url}
                      />
                    )}
                    {detail.connection_info.jupyter_token && (
                      <CopyableField
                        label="API Token"
                        value={detail.connection_info.jupyter_token}
                        masked
                      />
                    )}
                    {detail.connection_info.public_ip && (
                      <CopyableField
                        label="Public IP"
                        value={detail.connection_info.public_ip}
                      />
                    )}
                    {detail.connection_info.ports && (
                      <div>
                        <p className="text-sm text-muted-foreground font-gc-reg mb-1">Port Mappings</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(detail.connection_info.ports).map(([k, v]) => (
                            <span
                              key={k}
                              className="text-xs font-mono bg-muted px-2 py-1 rounded"
                            >
                              {k} → {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* vLLM Config */}
            <VllmConfigCard
              detail={detail}
              apiKey={apiKey}
              onSaved={loadDetail}
            />

            {/* Loaded Adapters */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-lg font-gc-demi-bold text-foreground mb-4">
                  Loaded Adapters
                </h2>
                {(detail.adapters || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No adapters loaded.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.adapters.map((adapter) => (
                      <div
                        key={adapter.model_id}
                        className="flex items-center justify-between rounded-md border border-border bg-background px-4 py-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge
                            variant={adapter.status === "ENABLED" ? "default" : "secondary"}
                            className="shrink-0"
                          >
                            {adapter.status}
                          </Badge>
                          <div className="min-w-0">
                            <p className="text-sm font-gc-med text-foreground truncate">
                              {adapterLabel(adapter)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Company {adapter.company_id}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 ml-4"
                          disabled={actionLoading}
                          onClick={() => setConfirmAction({ kind: "offload", adapter })}
                        >
                          <UnplugIcon className="w-3.5 h-3.5 mr-1.5" />
                          Offload
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* State History */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-lg font-gc-demi-bold text-foreground mb-4">
                  State History
                </h2>
                {(detail.state_history || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No state transitions recorded.</p>
                ) : (
                  <div className="space-y-2">
                    {(detail.state_history || []).map((h) => (
                      <div
                        key={h.id}
                        className="flex flex-wrap items-center gap-3 text-sm border-l-2 border-maitai-green/30 pl-4 py-2"
                      >
                        <span className="text-xs text-muted-foreground font-mono min-w-[160px]">
                          {formatTimestamp(h.changed_at)}
                        </span>
                        <span>
                          {h.old_status ? (
                            <span className="text-muted-foreground">{h.old_status}</span>
                          ) : (
                            <span className="text-muted-foreground italic">new</span>
                          )}
                          {" → "}
                          <strong>{h.new_status}</strong>
                        </span>
                        {h.changed_by && (
                          <Badge variant="secondary" className="text-xs">
                            {h.changed_by}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* vLLM Logs */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-gc-demi-bold text-foreground">vLLM Logs</h2>
                  <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
                    <RefreshCwIcon
                      className={`w-4 h-4 mr-1.5 ${logsLoading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </Button>
                </div>
                <pre className="text-xs bg-muted p-4 rounded-md max-h-[500px] overflow-auto whitespace-pre-wrap font-mono">
                  {logsLoading ? "Loading logs..." : logText || "(no logs)"}
                </pre>
              </CardContent>
            </Card>
          </>
        )}

        {/* Unified Confirmation Dialog */}
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(o) => { if (!o) setConfirmAction(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.kind === "start" && "Start instance?"}
                {confirmAction?.kind === "stop" && "Stop instance?"}
                {confirmAction?.kind === "destroy" && "Destroy instance?"}
                {confirmAction?.kind === "offload" && "Offload adapter?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.kind === "start" &&
                  "This will resume the instance on the provider. Billing will start again."}
                {confirmAction?.kind === "stop" &&
                  "This will stop the instance on the provider. Running workloads will be interrupted."}
                {confirmAction?.kind === "destroy" &&
                  "This terminates the provider instance and marks the row stopped. This cannot be undone."}
                {confirmAction?.kind === "offload" &&
                  `Unload "${confirmAction.adapter ? adapterLabel(confirmAction.adapter) : ""}" and disable the model?`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={
                  confirmAction?.kind === "destroy" || confirmAction?.kind === "offload"
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : ""
                }
                onClick={executeAction}
              >
                {confirmAction?.kind === "start" && "Start"}
                {confirmAction?.kind === "stop" && "Stop"}
                {confirmAction?.kind === "destroy" && "Destroy"}
                {confirmAction?.kind === "offload" && "Offload"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default InstanceAdminDetailPage;
